import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Box, Divider, Grid, LinearProgress, Typography } from '@mui/material';
import { TranslationKeys } from 'app/translations';
import type { BatchData, Connection } from 'app/types';
import { InsurerDataRequester } from 'app/types';
import type { PaginationProps, TableCellHeaderSortOptions } from 'app/components';
import { InsurerDataLogsAlert } from 'app/components';
import { CompareBatchFileUpload, CompareBatchTable, CompareBatchLoading } from './components';
import { ActionButtonStyled } from '../../../../../../components/Wizard/ Wizard.styles';
import { faSync } from '@fortawesome/free-solid-svg-icons';
import { GridItemStyled } from '../../../../../../components/ErrorPanel/ErrorPanel.styles';
import { runConnectionModule } from '../../../../Services';
import { useConnectionGetBatchQuery, useConnectionSearchLogsQuery } from '../../../../../../hooks';
import { Uuid } from '../../../../../../utils/uuid';

type CompareBatchPageContentProps = {
  connectionId: Connection['connectionId'] | undefined;
  batchId: string | undefined;
  batchCompareData: BatchData[];
  selected: Set<string>;
  onSelect: (selected: Set<string>) => void;
  onSearch: (inputValue: string) => void;
  onFilterMutationType: (filter: string) => void;
  onSortChange: (sortBy: Record<string, TableCellHeaderSortOptions>) => void;
  sortBy: Record<string, TableCellHeaderSortOptions>;
  startListeningForDataAfterUploadFile: (taskId: string) => void;
  approveBatch: () => void;
  archiveBatch: () => void;
  markAsCompleted: () => void;
  markSelectedAsCompleted: () => void;
  resendMutations: () => void;
  rejectMutations: () => void;
  declineBatch: () => void;
  paginationProps: PaginationProps;
  waitingForData?: boolean;
  isLoading?: boolean;
  searchQuery?: string;
  startPolling: () => void;
  isSynchronizing: boolean;
  mutationTypes: string[];
};

export const CompareBatchPageContent = ({
  connectionId,
  batchId,
  batchCompareData,
  selected,
  onSelect,
  onSearch,
  onFilterMutationType,
  onSortChange,
  sortBy,
  searchQuery,
  startListeningForDataAfterUploadFile,
  approveBatch,
  archiveBatch,
  markAsCompleted,
  markSelectedAsCompleted,
  resendMutations,
  rejectMutations,
  declineBatch,
  paginationProps,
  waitingForData = false,
  isLoading = false,
  startPolling,
  isSynchronizing,
  mutationTypes,
}: CompareBatchPageContentProps) => {
  const synchronizeWithInsurer = () => {
    runConnectionModule({
      connectionId: connectionId,
      moduleType: 'insurance_brydge_fetch_insurer_data',
      parameters: {
        batchId: batchId,
        insurerDataRequestId: Uuid.newV4(),
        requester: InsurerDataRequester.CompareBatch,
      },
    });
    startPolling();
  };

  const { data: status, refetch: refetchBatch } = useConnectionGetBatchQuery({
    variables: { batchId, connectionId },
    options: { enabled: !!batchId && !!connectionId },
  });

  const [logsPage, setLogsPage] = React.useState(1);
  const [logsPageSize, setLogsPageSize] = React.useState(10);
  const { data: logsResponse, refetch: refetchLogs } = useConnectionSearchLogsQuery({
    variables: {
      connectionId,
      requester: InsurerDataRequester.CompareBatch,
      correlationId: status?.insurerDataRequestId,
      page: logsPage,
      pageSize: logsPageSize,
    },
  });

  // When a synchronization ends, reload the batch (new insurerDataRequestId) and its logs
  const wasSynchronizingRef = React.useRef(isSynchronizing);
  React.useEffect(() => {
    if (wasSynchronizingRef.current && !isSynchronizing) {
      refetchBatch();
      setLogsPage(1);
      refetchLogs();
    }
    wasSynchronizingRef.current = isSynchronizing;
  }, [isSynchronizing, refetchBatch, refetchLogs]);

  const onLogsPaginationChange = React.useCallback<NonNullable<PaginationProps['onPaginationChange']>>(
    ({ page, rowSize }) => {
      if (page !== undefined) {
        setLogsPage(page);
      }
      if (rowSize !== undefined) {
        setLogsPageSize(rowSize);
        setLogsPage(1);
      }
    },
    [],
  );

  const logsPaginationProps = React.useMemo<PaginationProps>(
    () => ({
      page: logsPage,
      rowsPerPage: logsPageSize,
      onPaginationChange: onLogsPaginationChange,
      count: logsResponse?.totalItems,
    }),
    [logsPage, logsPageSize, onLogsPaginationChange, logsResponse?.totalItems],
  );

  return (
    <Box marginTop={2} position="relative">
      <CompareBatchLoading open={isSynchronizing} />
      <Grid container>
        <GridItemStyled item xs={12}>
          <ActionButtonStyled
            icon={faSync}
            messageId={TranslationKeys.global_synchronizeWithInsurer}
            onClick={synchronizeWithInsurer}
          />
        </GridItemStyled>
        <Grid item xs={12}>
          <Divider>
            <Typography textAlign={'center'} margin={2}>
              <FormattedMessage id={TranslationKeys.global_or} />
            </Typography>
          </Divider>
        </Grid>
        <Grid item xs={12}>
          <CompareBatchFileUpload
            isLoading={isLoading || waitingForData}
            batchId={batchId}
            connectionId={connectionId}
            startListeningForDataAfterUploadFile={startListeningForDataAfterUploadFile}
          />
        </Grid>
      </Grid>

      <Box marginTop={2}>
        <InsurerDataLogsAlert
          testId={'compare-batch-logs'}
          logs={logsResponse?.items || []}
          totalItems={logsResponse?.totalItems || 0}
          paginationProps={logsPaginationProps}
        />
      </Box>

      {waitingForData && (
        <Box marginTop={2} padding={2}>
          <Typography textAlign={'center'} marginBottom={1}>
            <FormattedMessage id={TranslationKeys.global_processing} />
          </Typography>
          <LinearProgress />
        </Box>
      )}

      {!waitingForData && (
        <CompareBatchTable
          batchId={batchId}
          connectionId={connectionId || ''}
          isLoading={isLoading || isSynchronizing}
          selected={selected}
          onSelect={onSelect}
          onSearch={onSearch}
          onFilterMutationType={onFilterMutationType}
          onSortChange={onSortChange}
          sortBy={sortBy}
          searchQuery={searchQuery}
          synchronizeWithInsurer={synchronizeWithInsurer}
          batchCompareData={batchCompareData}
          approveBatch={approveBatch}
          archiveBatch={archiveBatch}
          markAsComplete={markAsCompleted}
          markSelectedAsComplete={markSelectedAsCompleted}
          resendMutations={resendMutations}
          rejectMutations={rejectMutations}
          declineBatch={declineBatch}
          paginationProps={paginationProps}
          batchStatus={status?.data || 'draft'}
          mutationTypes={mutationTypes}
        />
      )}
    </Box>
  );
};
